/**
 * Label text: cleaning, folding, and filler detection. LAYER 0 — pure
 * functions, ZERO project imports.
 *
 * TWO DIFFERENT JOBS, DELIBERATELY NOT ONE FUNCTION
 * -------------------------------------------------
 * `cleanStoredLabel` WRITES DATA. It strips edge punctuation and nothing else,
 * because its output is persisted as a dataset label — every character it
 * removes is a character gone from the corpus forever.
 *
 * `foldForCompare` NEVER WRITES. It folds case, Latin and Arabic diacritics,
 * hamza/yeh/teh-marbuta spelling variants and all punctuation down to a
 * skeleton, so two transcriptions of the same sound compare equal. Its output
 * is lossy on purpose and must never reach the `segments` store.
 *
 * Before the restructure these lived in two services with no statement anywhere
 * that they were different, under names (`cleanWordText`, `normalizeLabel`)
 * that suggested they were interchangeable. They are not.
 *
 * `foldForCompare` also de-collides a name: `src/model/ids.ts` has its own
 * `normalizeLabel` with different punctuation handling, and the two were never
 * substitutable. See `src/model/README.md`.
 */

/* ------------------------------------------------------------------ */
/* Cleaning — WRITES stored labels                                     */
/* ------------------------------------------------------------------ */

/**
 * WhisperX writes punctuation onto the word itself — it returns "اه،" (with
 * the Arabic comma), not "اه". Testing that against the filler list failed
 * every time, so no clip was ever tagged as a filler and "force fillers" had
 * nothing to protect. Strip the punctuation before matching, and store the
 * clean form: a dataset label should be the word, not the word plus a comma.
 */
const EDGE_PUNCT = /^[\s.,!?;:"'“”«»…،؛؟]+|[\s.,!?;:"'“”«»…،؛؟]+$/g;

/**
 * Strip edge punctuation from a word and trim it. The result is STORED.
 *
 * Was `alignedFirstPass.cleanWordText`.
 */
export function cleanStoredLabel(text: string): string {
  return text.replace(EDGE_PUNCT, '').trim();
}

/* ------------------------------------------------------------------ */
/* Folding — COMPARISON only, never stored                             */
/* ------------------------------------------------------------------ */

/** Latin combining accents left over after NFKD. */
const LATIN_MARKS = /[̀-ͯ]/g;

/** Arabic harakat, tanwin, superscript alef, Quranic marks and tatweel. */
const ARABIC_MARKS = /[ؐ-ًؚ-ٰٟۖ-ۭـ]/g;

/**
 * Orthographic variants that must not count as a difference.
 *
 * Arabic writes the same sound several ways and no two transcribers pick the
 * same one: hamza carriers, alef maqsura vs yeh, teh marbuta vs heh, plus the
 * Persian letter forms a multilingual model reaches for maybe one time in ten.
 * The transliteration in each comment is there because bidirectional source is
 * near-impossible to proofread in a diff.
 */
const LETTER_FOLDS: Record<string, string> = {
  'أ': 'ا', // alef + hamza above -> alef
  'إ': 'ا', // alef + hamza below -> alef
  'آ': 'ا', // alef + madda       -> alef
  'ٱ': 'ا', // alef wasla         -> alef
  'ؤ': 'و', // waw + hamza        -> waw
  'ئ': 'ي', // yeh + hamza        -> yeh
  'ء': '', //       standalone hamza   -> nothing (it has no skeleton)
  'ى': 'ي', // alef maqsura       -> yeh
  'ی': 'ي', // farsi yeh          -> yeh
  'ة': 'ه', // teh marbuta        -> heh
  'ک': 'ك', // keheh              -> kaf
};

/** Covers every key of {@link LETTER_FOLDS}; non-keys fall through untouched. */
const FOLDABLE = /[ء-ی]/g;

/**
 * Fold a label down to the skeleton two transcriptions of the same sound must
 * share: case, diacritics (Latin AND Arabic), hamza/yeh/teh-marbuta spelling,
 * punctuation and whitespace all stop mattering.
 *
 * LOSSY BY DESIGN — comparison only. Never persist this. Was
 * `clipQa.normalizeLabel`.
 */
export function foldForCompare(text: string): string {
  if (!text) return '';
  let out = text.normalize('NFKD').replace(LATIN_MARKS, '');
  out = out.replace(ARABIC_MARKS, '');
  out = out.replace(FOLDABLE, (ch) => LETTER_FOLDS[ch] ?? ch);
  out = out.toLowerCase();
  // Punctuation (Latin, Arabic, and the quotes models love) becomes a break.
  out = out.replace(/[^\p{L}\p{N}\s]/gu, ' ');
  return out.replace(/\s+/g, ' ').trim();
}

/** Collapse doubled letters: "ummm" and "um" are the same hesitation. */
export function squash(text: string): string {
  return text.replace(/(.)\1+/gu, '$1');
}

/* ------------------------------------------------------------------ */
/* Fillers — two tests, deliberately different                         */
/* ------------------------------------------------------------------ */

/**
 * SPELLING test: does the raw word LOOK like a hesitation? Run on text that
 * has not been folded, so it must spell out the repeats itself (`um+`).
 *
 * This alphabet is the SUPERSET of the four private copies that existed across
 * services before the restructure; the narrow ones were missing entries
 * (`er`, `ah`, Arabic `اها`/`همم`) and that was a transcription oversight, not
 * a policy. A word that is a filler must stay a filler when a human drags it
 * across a clip boundary, which only holds if every site asks the same question.
 *
 * Fillers, in both scripts — also used to tag WhisperX words, which carry no kind.
 */
const FILLER_RE = /^(um+|uh+|hm+|mm+|er+|ah+|امم+|اه+|ايه|اها|همم+)$/i;

/** Was `alignedFirstPass.isFillerWord`. */
export function isFillerSpelling(text: string): boolean {
  return FILLER_RE.test(cleanStoredLabel(text));
}

/**
 * Hesitation sounds, already squashed and normalised. Both scripts are listed
 * because the model picks a script per clip: an Arabic speaker's "mmm" comes
 * back as "hmm" about as often as it comes back in Arabic letters.
 */
const FILLER_TOKENS = new Set([
  // Latin script
  'um', 'uhm', 'uh', 'u', 'hm', 'm', 'mhm', 'mhmm', 'umhum', 'uhhuh', 'huh',
  'er', 'erm', 'ah', 'eh', 'oh', 'ha', 'a', 'e', 'o', 'h', 'tsk',
  'breath', 'breathing', 'sigh', 'inhale', 'exhale', 'hesitation', 'filler',
  // Arabic script, post-fold
  'ام', // am
  'اه', // ah
  'هم', // hm
  'ايه', // eyh
  'اي', // ey
  'يه', // yh
  'م', // m
  'ا', // a
  'ه', // h
  'ع', // ayn
]);

/**
 * POST-FOLD test: is this label, once folded and squashed, one of the known
 * hesitation tokens?
 *
 * Deliberately NOT the same question as {@link isFillerSpelling}: this one runs
 * after {@link foldForCompare} has already removed diacritics and folded Arabic
 * letter forms, so it matches skeletons ('اه') rather than spellings ('اه+').
 * Both are kept because both are correct for their own caller.
 */
export function isFillerLabel(text: string): boolean {
  const t = squash(foldForCompare(text).replace(/\s+/g, ''));
  return t.length > 0 && FILLER_TOKENS.has(t);
}
